<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <form-separator v-if="showHourlyConcentration || showMinConcentration">
    <template #icon>
      <icon-colored-demographic />
    </template>
    {{ capitalize(i18n.t('names.audienceConcentration')) }}
  </form-separator>
  <div class="minimum-concentration-factor">
    <template v-if="showHourlyConcentration">
      <template v-if="isInitializing">
        <div class="icon text-center">
          <loader-small />
          {{ capitalize(i18n.t('messages.loadingConcentration')) }}
        </div>
      </template>
      <template v-else>
        <!-- SCREENS WITH ZERO CONCENTRATION -->
        <div
          v-if="invalidCount"
          class="alert alert-warning icon-sm flex-h">
          <icon-warning />
          <span>
            {{ i18n.t('messages.missingConcentration', [invalidCount, totalCount], 2) }}
          </span>
        </div>
        <!-- HOURLY CONCENTRATIONS -->
        <button
          id="view_audience_concentration"
          style="width: fit-content"
          class="btn btn-primary-hollow mx-auto"
          @click="showUnitsConcentrationsModal">
          <icon-chart-bar />
          {{ capitalize(i18n.t('messages.seeHourlyConcentrations')) }}
        </button>
      </template>
    </template>
    <min-concentration-factor
      :show-apply="showApplyConcentration"
      :column-width="columnWidth"
      :is-disabled="isDisabled"
      :entity="entity"
      @changed="(data) => emits('changed', data)"
      @apply="applyMinimumConcentration" />
  </div>
  <modal
    id="units_concentrations_modal"
    :visible="isUnitsConcentrationsModalVisible"
    :title="capitalize(i18n.t('messages.seeHourlyConcentrations'))"
    :use-full-width="true"
    :use-full-height="true"
    :no-padding="true"
    @confirmed="isUnitsConcentrationsModalVisible = false"
    @cancelled="isUnitsConcentrationsModalVisible = false">
    <template #modal-after-form>
      <div class="content">
        <audience-insights
          :units="units"
          :invalid-units="invalidUnits"
          :demographic-i-ds="entity.demographicIDs"
          :average-audience-concentration="averageConcentrationFormatted"
          @update-unit-i-ds="updateValidUnitIDs" />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
  import axios from 'axios';
  import { capitalize, computed, nextTick, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import GoogleAnalyticsApi from '@/common/apis/GoogleAnalyticsApi';
  import { FormSeparator } from '@/common/components/controls';
  import LoaderSmall from '@/common/components/controls/LoaderSmall.vue';
  import Modal from '@/common/components/controls/Modal.vue';
  import { IconColoredDemographic } from '@/common/icons/colored';
  import { IconChartBar, IconWarning } from '@/common/icons/monochrome';
  import { FilterComparisonOperator, LineItem, Plan } from '@/common/models';
  import { AudienceConcentrationPerUnitData } from '@/common/models/audience/AudienceConcentrationPerUnit';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { areArraysEqual, getObjectDifferences, isObjectEmpty } from '@/globals';
  import AudienceInsights from '@/org-app/components/audienceConcentration/AudienceInsights.vue';
  import {
    getBatchedConcentrationData,
    getBatchedConcentrationDataFiltered,
  } from '@/org-app/components/audienceConcentration/ConcentrationUtils';
  import MinConcentrationFactor from '@/org-app/components/audienceConcentration/MinConcentrationFactor.vue';
  import formatNumber from '@/utils/NumberFormatter';
  import { mapOrgAppActions } from '@/utils/StateMapping';
  import UserNotifications from '@/utils/UserNotifications';

  //
  // Setup
  //
  const i18n = useI18n();
  const props = withDefaults(
    defineProps<{
      entity: LineItem | Plan;
      averageConcentration: number;
      columnWidth: number;
      showHourlyConcentration: boolean;
      showMinConcentration: boolean;
      showApplyConcentration: boolean;
      isDisabled: boolean;
    }>(),
    { columnWidth: 8 },
  );
  const emits = defineEmits<{
    (e: 'changed', val: { minConcentrationFactor: number; minConcentrationValue: number }): void;
    (e: 'apply', val: number[]): void;
  }>();
  const { getTargetedUnitAndSiteIDs } = mapOrgAppActions();

  //
  // Mutable Data
  //
  const allUnitIDs = ref<number[]>([]);
  const units = ref<AudienceConcentrationPerUnitData[]>([]);
  const isUnitsConcentrationsModalVisible = ref(false);
  const isInitializing = ref(true);
  const isMounted = ref(false);

  //
  // Computed Properties
  //
  const invalidUnits = computed(() => units.value.filter((u) => u.averageConcentrations < 0.001));
  const totalCount = computed(() => units.value.length);
  const invalidCount = computed(() => invalidUnits.value.length);
  const averageConcentrationFormatted = computed(() => {
    return formatNumber(props.averageConcentration, '0.00%');
  });
  const basePayload = computed(() => ({
    unitIDs: allUnitIDs.value,
    demographicIDs: props.entity.demographicIDs,
  }));

  // Data fetching
  let cancelTokenSource = axios.CancelToken.source();
  async function fetchUnits() {
    isInitializing.value = true;
    if (props.showHourlyConcentration && props.entity.demographicIDs?.length) {
      await fetchAllUnitIDs();
      const { data } = await getBatchedConcentrationData({
        unitIDs: allUnitIDs.value,
        demographicIDs: props.entity.demographicIDs,
        cancelTokenSource,
      });
      units.value = data ?? [];
    } else {
      units.value = [];
    }
    isInitializing.value = false;
  }

  async function fetchAllUnitIDs() {
    // Override allUnitIDs with entity unit IDs
    if (props.entity.unitIDs?.length) {
      allUnitIDs.value = props.entity.unitIDs;
    } else {
      // Otherwise we fetch based on targeting params
      const response = await getTargetedUnitAndSiteIDs({
        data: props.entity.targetParamsEstimatedDeliveryStatistics,
        cancelTokenSource,
      });
      if (response) {
        allUnitIDs.value = response.unitIDs;
      }
    }
  }

  // Helpers
  function showUnitsConcentrationsModal() {
    // Need start / end date defined
    if (props.entity.startDate === null || props.entity.endDate === null) {
      UserNotifications.error({ message: i18n.t('messages.failedToGetTargetedScreens') });
    } else {
      GoogleAnalyticsApi.sendEvent('Audience Concentrations Per Unit', {
        category: 'Audience Concentrations Per Unit',
        value: 'Audience Concentrations',
      });
      isUnitsConcentrationsModalVisible.value = true;
    }
  }

  // Mutation
  const lastExcludedUnits = ref<number[]>();
  function excludeUnits(unitIDs: number[]) {
    lastExcludedUnits.value = unitIDs;
    emits('apply', unitIDs);
  }
  function updateValidUnitIDs() {
    const invalidUnitIDs = invalidUnits.value.map((u) => u.unitID);
    units.value = units.value.filter((u) => u.averageConcentrations >= 0.001);
    allUnitIDs.value = units.value.map((u) => u.unitID);
    excludeUnits(invalidUnitIDs);
  }
  async function applyMinimumConcentration(value: number) {
    isInitializing.value = true;
    // Lazy load when we aren't showing hourly concentration data
    if (!allUnitIDs.value?.length) {
      await fetchAllUnitIDs();
    }
    const oldUnitIDs = units.value.map((u) => u.unitID);
    units.value = await getBatchedConcentrationDataFiltered({
      ...basePayload.value,
      value,
      filter: FilterComparisonOperator.GREATER_THAN_OR_EQUAL,
      showLoader: true,
      cancelTokenSource,
    }).then((r) => r.data);
    if (units.value?.length) {
      const unitIDs = units.value.map((u) => u.unitID);
      const excludedIDs = oldUnitIDs.filter((id) => !unitIDs.includes(id));
      emits('apply', excludedIDs);
    } else {
      const message = capitalize(i18n.t('messages.noScreensAboveTargetConcentration'));
      UserNotifications.error({ message });
      GlobalEventBus.$emit(GlobalEvents.error, message);
    }
    isInitializing.value = false;
  }

  // Setup
  onMounted(async () => {
    // Fetch valid units
    if (props.entity.demographicIDs?.length) {
      await fetchUnits();
    }
    await nextTick();
    isMounted.value = true;
  });
  // Watchers
  watch(
    () => props.entity.demographicIDs,
    async (val, old) => {
      if (!areArraysEqual(val, old)) {
        cancelTokenSource.cancel();
        cancelTokenSource = axios.CancelToken.source();
        await fetchUnits();
      }
    },
  );
  watch(
    () => props.entity.targetParamsEstimatedDeliveryStatistics.unit_targeting,
    async (value, old) => {
      if (isMounted.value) {
        // Special case for unit IDs. We generally want to catch this change, except in one case.
        // This event will cyclically catch its own change from applying exclusions, we have to filter out this case.
        const oldPatched = { ...old, unit_exclusion_ids: lastExcludedUnits.value };
        const differences = getObjectDifferences(oldPatched, value);
        if (!isObjectEmpty(differences)) {
          cancelTokenSource.cancel();
          cancelTokenSource = axios.CancelToken.source();
          await fetchUnits();
        }
      }
    },
  );
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  .minimum-concentration-factor {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }
</style>
